import React, { type ReactNode, useMemo, useState } from "react";
import type { DateRangePreset } from "@helpers/date";
import {
    DATE_RANGE_PRESETS_BY_ID,
    formatExplicitDateRangeLabel,
    getDateRangeLabel,
    VISIBLE_PRESETS,
    ALL_TIME_PRESET_ID,
} from "@helpers/date";
import { useConstant } from "@hooks/useConstant";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import {
    parseDateOnly,
    toDateOnly,
} from "@design-system/DatePicker/DatePicker.lib";
import type {
    DateOnlyRange,
    DatePickerFieldControlProps,
    DatePickerPreset,
} from "@design-system/DatePicker/DatePicker.types";
import { Button } from "@design-system/Button/Button";
import type { ValueControlProps } from "@design-system/formControl/formControl.types";
import type { DateOnlyDateRange } from "./DateRangeFilterNext.lib";

const toPresetLabel = (preset: DateRangePreset, today: Date) =>
    preset.labelFn?.(today) ?? preset.label ?? preset.id;

function resolveTriggerLabel({
    selectedPresetId,
    presets,
    start,
    end,
    today,
}: {
    selectedPresetId: string | null | undefined;
    presets: ReadonlyArray<DatePickerPreset<DateOnlyRange>>;
    start: Date | undefined;
    end: Date | undefined;
    today: Date;
}): string {
    if (selectedPresetId === undefined) {
        return getDateRangeLabel(start, end);
    }
    if (!selectedPresetId) {
        return formatExplicitDateRangeLabel(start, end);
    }
    if (selectedPresetId === ALL_TIME_PRESET_ID) {
        return "All time";
    }
    const preset = presets.find(({ id }) => id === selectedPresetId);
    if (preset) {
        return preset.label;
    }
    const knownPreset = DATE_RANGE_PRESETS_BY_ID[selectedPresetId];
    if (knownPreset) {
        return toPresetLabel(knownPreset, today);
    }
    return formatExplicitDateRangeLabel(start, end);
}

/**
 * The standard control props a page filter needs to join a form `Field`.
 * `ref` points at the trigger button; `name` submits the range the way
 * `DatePicker` range modes do (`yyyy-MM-dd/yyyy-MM-dd`, `""` for all time).
 */
type DateRangeFilterNextControlProps = Pick<
    ValueControlProps<DateOnlyDateRange, HTMLButtonElement>,
    "onBlur" | "name" | "ref" | "invalid" | "disabled"
>;

export interface DateRangeFilterNextProps extends DateRangeFilterNextControlProps {
    /**
     * Calendar-day range as yyyy-MM-dd strings. Date objects never cross
     * this component's boundary, so values cannot shift across timezones.
     */
    dateRange: DateOnlyDateRange;
    /** `presetId` is set only when the user clicked a preset other than "All time". */
    onChange: (
        value: DateOnlyDateRange,
        isAllTime?: boolean,
        presetId?: string,
    ) => void;
    requireSelection?: boolean;
    pinnedPresets?: readonly string[];
    onTogglePin?: (id: string) => void;
    buttonClass?: string;
    /** Shown before the trigger's label, e.g. `<CalendarIcon />`. */
    leftIcon?: ReactNode;
    /** Listed before the standard presets, for a page's own default range. */
    leadingPresets?: ReadonlyArray<DatePickerPreset<DateOnlyRange>>;
    /** Appended after the standard presets, before "All time". */
    extraPresets?: ReadonlyArray<DatePickerPreset<DateOnlyRange>>;
    /** Opt-in: highlight the preset with this id (none for `null`) instead of matching dates. */
    selectedPresetId?: string | null;
    /** Labels the trigger instead of `dateRange`, e.g. while a chart's window is dragged. */
    previewRange?: DateOnlyDateRange | null;
    /** Id and aria props from `Field.Control render`, forwarded to the trigger. */
    fieldControlProps?: DatePickerFieldControlProps;
}

export const DateRangeFilterNext: React.FC<DateRangeFilterNextProps> = ({
    dateRange,
    onChange,
    requireSelection,
    pinnedPresets,
    onTogglePin,
    buttonClass,
    leftIcon,
    leadingPresets,
    extraPresets,
    selectedPresetId,
    previewRange,
    fieldControlProps,
    onBlur,
    name,
    ref,
    invalid = false,
    disabled,
}) => {
    const today = useConstant(() => new Date());

    // Holds the half-open range while the user is mid-selection (first month
    // clicked, second pending). Only complete ranges are committed to the
    // parent: parents that normalize their filters (e.g. the accounting pages
    // re-fill a missing end date from cached defaults) would otherwise echo
    // back a different complete range and clobber the in-progress selection.
    const [draftRange, setDraftRange] = useState<DateOnlyRange | undefined>(
        undefined,
    );

    const presets = useMemo(() => {
        const visiblePresets = VISIBLE_PRESETS.flatMap((id) => {
            const preset = DATE_RANGE_PRESETS_BY_ID[id];
            if (!preset) {
                return [];
            }
            return [
                {
                    id: preset.id,
                    label: toPresetLabel(preset, today),
                    value: {
                        from: toDateOnly(preset.getStart(today)) ?? "",
                        to: toDateOnly(preset.getEnd(today)) ?? "",
                    },
                },
            ];
        });

        const allPresets = [
            ...(leadingPresets ?? []),
            ...visiblePresets,
            ...(extraPresets ?? []),
        ];
        if (requireSelection) {
            return allPresets;
        }

        return [
            ...allPresets,
            { id: ALL_TIME_PRESET_ID, label: "All time", value: {} },
        ];
    }, [today, requireSelection, leadingPresets, extraPresets]);

    const value: DateOnlyRange | undefined =
        dateRange.start || dateRange.end
            ? { from: dateRange.start, to: dateRange.end }
            : undefined;

    const labelRange = previewRange ?? dateRange;
    const label = resolveTriggerLabel({
        selectedPresetId: previewRange ? undefined : selectedPresetId,
        presets,
        start: parseDateOnly(labelRange.start),
        end: parseDateOnly(labelRange.end),
        today,
    });

    return (
        <DatePicker
            mode="monthRange"
            variant="button"
            value={draftRange ?? value}
            onValueChange={(next, presetId) => {
                if (next?.from && !next.to) {
                    setDraftRange(next);
                    return;
                }
                setDraftRange(undefined);
                const isAllTime = !next?.from && !next?.to;
                onChange(
                    isAllTime ? {} : { start: next?.from, end: next?.to },
                    isAllTime,
                    isAllTime ? undefined : presetId,
                );
            }}
            onOpenChange={() => setDraftRange(undefined)}
            onBlur={onBlur}
            name={name}
            disabled={disabled}
            presets={presets}
            selectedPresetId={selectedPresetId}
            showPresets
            showAdjacentYearButtons
            clearable={false}
            required={requireSelection}
            pinnablePresets={onTogglePin !== undefined}
            defaultPinnedPresetIds={pinnedPresets}
            onPinnedPresetIdsChange={
                onTogglePin
                    ? (ids) => {
                          const previous = pinnedPresets ?? [];
                          const toggledId =
                              ids.find((id) => !previous.includes(id)) ??
                              previous.find((id) => !ids.includes(id));
                          if (toggledId) {
                              onTogglePin(toggledId);
                          }
                      }
                    : undefined
            }
            placeholder="All time"
            trigger={
                <Button
                    ref={ref}
                    variant="primary"
                    size="md"
                    className={buttonClass}
                    leftIcon={leftIcon}
                    {...fieldControlProps}
                    aria-invalid={
                        invalid ||
                        fieldControlProps?.["aria-invalid"] ||
                        undefined
                    }
                >
                    {label || "All time"}
                </Button>
            }
        />
    );
};
